/**
 * OIDC Authorization Code + PKCE against knowit-auth.
 *
 * Slate is a PUBLIC client (`slate-web`, no secret) because it is a browser app. It needs its
 * own registration rather than reusing the platform session: the KnowIt session cookie is
 * host-only (IS_CLOUD_HOSTED is off), so a kms.docit.pro session is never sent here.
 *
 * Same-origin by construction — Caddy proxies `/connect/*` on this host to knowit-auth — so
 * both the authorize navigation and the token exchange avoid CORS entirely. That also sidesteps
 * the discovery document, which reports `http://` endpoints because the service sits behind a
 * TLS-terminating proxy.
 *
 * No token value is ever logged or included in a thrown error.
 */
import {
  clearSession,
  isExpired,
  readSession,
  type StoredSession,
  writeSession,
} from "./tokenStore";

const CLIENT_ID = "slate-web";
const SCOPE = "openid profile email";

/** Empty = same origin. Caddy (prod) and vite's proxy (dev) both map /connect/* to knowit-auth. */
const AUTH_BASE = (import.meta.env.VITE_AUTH_BASE ?? "").replace(/\/+$/, "");

// `slate.` is the ESTATE storage namespace, not a brand — coil already writes these same
// slate.auth.pkce.* keys at my.app.co.il (same docit-api source), so an in-flight login
// survives the appcoil cutover. Do not re-prefix or alias these keys for a site profile: it
// would mass-sign-out my.app.co.il and risk breaking an in-progress PKCE exchange.
const VERIFIER_KEY = "slate.auth.pkce.verifier";
const STATE_KEY = "slate.auth.pkce.state";
const RETURN_KEY = "slate.auth.return_to";

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthError";
  }
}

function base64Url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of view) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(bytes = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(digest);
}

export function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

/**
 * Same-origin relative paths only. `returnTo` is echoed back through storage and the URL, so
 * an unchecked value is an open redirect; `//evil.com` is a protocol-relative URL, not a path.
 * Auth pages are refused too — returning to /login after signing in just shows the login page
 * again, which reads exactly like a failed sign-in.
 */
const AUTH_PATHS = ["/login", "/callback", "/logout", "/auth/callback"];

export function safeReturnTo(candidate: string | null | undefined): string {
  if (!candidate?.startsWith("/") || candidate.startsWith("//")) {
    return "/";
  }
  const path = candidate.split("?")[0] ?? "/";
  return AUTH_PATHS.includes(path) ? "/" : candidate;
}

/**
 * The upstream identity providers the auth server offers (`/connect/providers`). A provider
 * appears only once it is configured there, so a button built from this list never leads to a
 * dead end. Fails soft to "none": the default sign-in still works without it.
 */
export interface AuthProviderOption {
  id: string;
  displayName: string;
}

export async function fetchAuthProviders(): Promise<AuthProviderOption[]> {
  try {
    const response = await fetch(`${AUTH_BASE}/connect/providers`, { credentials: "omit" });
    if (!response.ok) {
      return [];
    }
    const body: unknown = await response.json();
    return Array.isArray(body)
      ? body
          .filter((p): p is AuthProviderOption => typeof p?.id === "string")
          .map((p) => ({ id: p.id, displayName: String(p.displayName ?? p.id) }))
      : [];
  } catch {
    return [];
  }
}

/**
 * Full-page redirect into the authorization endpoint. Never returns. `provider` asks the auth
 * server for a specific upstream (`acr_values=provider:<id>`, e.g. "github"); absent = default.
 */
export async function signIn(
  returnTo = safeReturnTo(window.location.pathname + window.location.search),
  provider?: string,
) {
  const verifier = randomString();
  const state = randomString(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const url = new URL(`${AUTH_BASE}/connect/authorize`, window.location.origin);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("scope", SCOPE);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await challengeFor(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  if (provider) {
    url.searchParams.set("acr_values", `provider:${provider}`);
  }

  window.location.assign(url.toString());
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

function store(payload: TokenResponse): StoredSession {
  const session: StoredSession = {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token ?? null,
    expiresAt: Date.now() + (payload.expires_in ?? 3600) * 1000,
  };
  writeSession(session);
  return session;
}

async function postToken(body: Record<string, string>): Promise<TokenResponse> {
  const response = await fetch(`${AUTH_BASE}/connect/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(body).toString(),
  });

  if (!response.ok) {
    // Deliberately does not include the body: it can echo the code or token.
    throw new AuthError(`Token endpoint returned ${response.status}`);
  }
  return (await response.json()) as TokenResponse;
}

/**
 * Completes the redirect. Returns where the user was heading before sign-in so the caller can
 * restore it.
 */
export async function completeSignIn(search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const error = params.get("error");
  if (error) {
    throw new AuthError(params.get("error_description") ?? error);
  }

  const code = params.get("code");
  const state = params.get("state");
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const returnTo = safeReturnTo(sessionStorage.getItem(RETURN_KEY));

  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  if (!code || !verifier) {
    throw new AuthError("Sign-in response was incomplete");
  }
  // CSRF: a response whose state does not match the one we generated is not ours.
  if (!state || state !== expectedState) {
    throw new AuthError("Sign-in state did not match");
  }

  store(
    await postToken({
      grant_type: "authorization_code",
      code,
      client_id: CLIENT_ID,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  );

  return returnTo;
}

let refreshInFlight: Promise<StoredSession | null> | null = null;

async function refresh(session: StoredSession): Promise<StoredSession | null> {
  if (!session.refreshToken) {
    return null;
  }
  try {
    return store(
      await postToken({
        grant_type: "refresh_token",
        refresh_token: session.refreshToken,
        client_id: CLIENT_ID,
      }),
    );
  } catch {
    clearSession();
    return null;
  }
}

/**
 * A usable access token, renewing it if it has expired. Concurrent callers share one refresh
 * so a page that fires six queries at once does not spend six refresh tokens.
 */
export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  const session = readSession();
  if (!session) {
    return null;
  }
  if (!forceRefresh && !isExpired(session)) {
    return session.accessToken;
  }

  refreshInFlight ??= refresh(session).finally(() => {
    refreshInFlight = null;
  });

  return (await refreshInFlight)?.accessToken ?? null;
}

export function isSignedIn(): boolean {
  return readSession() !== null;
}

export function signOut(): void {
  clearSession();
}
